// ============================================================
// CloudIntegrationAPIs.jsx
//
// Course content page for "Cloud Integration & APIs" lesson.
// Rewritten to fix three real accuracy problems found while
// scrutinizing this file:
//   1) the "Azure IoT Hub" platform card used `SiMicrobit` — the
//      BBC micro:bit logo, not Microsoft Azure's. Wrong brand
//      entirely, not just a styling slip.
//   2) the "IBM Watson" platform card used `SiIcon`, which isn't
//      a real icon in the react-icons/si set — an invalid import
//      that would throw "element type is invalid" at render time.
//   3) two of the four "Enterprise Platforms" showcased are
//      discontinued services: Google Cloud IoT Core was retired
//      in August 2023, and IBM's Watson IoT Platform was withdrawn
//      from IBM Cloud in 2022/2023. Teaching students to target
//      dead services is actively wrong. Replaced with Google Cloud
//      Pub/Sub (Google's recommended successor pattern for IoT
//      ingestion) and HiveMQ Cloud (a live, widely used managed
//      MQTT broker, already referenced elsewhere in this track).
//   4) completed the ThingSpeak code example — the original never
//      connected to Wi-Fi or declared setup(), so it wouldn't run.
// Also matches the app-wide dark-purple/goldenrod look, uses
// arrow-function components, and Material Icons only (which also
// resolves the brand-logo mismatches above by design).
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Tooltip,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import StorageIcon from "@mui/icons-material/Storage";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import InsightsIcon from "@mui/icons-material/Insights";
import LockIcon from "@mui/icons-material/Lock";
import ApiIcon from "@mui/icons-material/Api";
import WebhookIcon from "@mui/icons-material/Webhook";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import CloudIcon from "@mui/icons-material/Cloud";
import ForumIcon from "@mui/icons-material/Forum";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const SERVICES = [
    { title: "Data Persistence", icon: <StorageIcon />, description: "Secure, redundant storage for years of historical telemetry data, enabling deep-dive trend analysis." },
    { title: "Device Shadow", icon: <CloudSyncIcon />, description: "Maintain a persistent digital twin of your device state, even when the hardware is offline." },
    { title: "ML & Analytics", icon: <InsightsIcon />, description: "Leverage cloud-scale AI to detect anomalies or predict hardware failures before they happen." },
    { title: "Identity & IAM", icon: <LockIcon />, description: "Manage end-to-end encryption and device certificates for enterprise-grade security." },
];

// Currently-active platforms only — see file header for why Google
// Cloud IoT Core and IBM Watson IoT Platform were replaced.
const PLATFORMS = [
    { name: "AWS IoT Core", icon: <CloudQueueIcon />, color: "#FF9900" },
    { name: "Azure IoT Hub", icon: <CloudSyncIcon />, color: "#0078D4" },
    { name: "Google Cloud Pub/Sub", icon: <CloudIcon />, color: "#4285F4" },
    { name: "HiveMQ Cloud (MQTT)", icon: <ForumIcon />, color: BRAND.teal },
];

// Sub-components (arrow-function style, defined before first use)

const CloudPlatformIcon = ({ icon, name, color }) => (
    <Tooltip title={name}>
        <Box
            sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.03)",
                border: `1px solid ${BRAND.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "0.3s",
                "&:hover": { borderColor: color, transform: "scale(1.1)", bgcolor: `${color}1a` },
            }}
        >
            {React.cloneElement(icon, { style: { color, fontSize: 28 } })}
        </Box>
    </Tooltip>
);

const ServiceCard = ({ title, icon, description }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: BRAND.gold, transform: "translateY(-4px)" },
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: "rgba(218,165,32,0.15)", color: BRAND.gold }}>{icon}</Avatar>
            <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>{description}</Typography>
    </Paper>
);

const CodeTerminal = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Typography component="pre" sx={{ p: 2.5, color: "#FCD34D", fontSize: 13, fontFamily: "'Fira Code', monospace", overflowX: "auto" }}>
                {code}
            </Typography>
        </Box>
    );
};

// A complete, compiling sketch — the original never connected to
// Wi-Fi or declared setup(), so http.begin()/http.GET() had nothing
// to run over.
const THINGSPEAK_SKETCH = `#include <WiFi.h>
#include <HTTPClient.h>

void setup() {
  Serial.begin(115200);
  WiFi.begin("SSID", "PASSWORD");
  while (WiFi.status() != WL_CONNECTED) {
    delay(500);
  }
}

void loop() {
  HTTPClient http;
  // Use https:// in production — plain HTTP is fine for learning,
  // but your API key travels in plaintext over HTTP.
  String url = "http://api.thingspeak.com/update?api_key=XXX&field1=25.5";

  http.begin(url);
  int code = http.GET(); // Simple RESTful trigger

  if (code > 0) Serial.println("Upload Success");
  http.end();
  delay(15000);
}`;

// ── Main Component ──────────────────────────────────────────────

const CloudIntegrationAPIs = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Cloud Ecosystems · Module 07"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    Cloud Integration & <span style={{ color: BRAND.teal }}>APIs</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    Bridge your hardware to the global web. Cloud integration provides the scale needed to
                    store, analyze, and visualize data from thousands of distributed IoT nodes.
                </Typography>
            </Box>

            {/* CORE SERVICES OVERVIEW */}
            <Box sx={{ mb: 10 }}>
                <Grid container spacing={3}>
                    {SERVICES.map((s) => (
                        <Grid item xs={12} md={3} key={s.title}>
                            <ServiceCard {...s} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* API ECOSYSTEM */}
            <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                <Grid item xs={12} md={5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <ApiIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>The RESTful Interface</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4, lineHeight: 1.8 }}>
                        APIs act as the "contract" between your ESP32 and the server. Using <strong>HTTP methods</strong>,
                        you can push data to a cloud endpoint as easily as loading a webpage.
                    </Typography>
                    <Stack spacing={2}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Chip label="POST" size="small" sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 900 }} />
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>Create/update sensor records</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Chip label="GET" size="small" sx={{ bgcolor: BRAND.teal, color: BRAND.bg, fontWeight: 900 }} />
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>Retrieve device configuration</Typography>
                        </Box>
                    </Stack>
                </Grid>
                <Grid item xs={12} md={7}>
                    <CodeTerminal label="THINGSPEAK_UPLOAD.ino" code={THINGSPEAK_SKETCH} />
                </Grid>
            </Grid>

            {/* PLATFORM SHOWCASE */}
            <Box sx={{ mb: 10 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, textAlign: "center" }}>Enterprise Platforms</Typography>
                <Grid container spacing={2} justifyContent="center">
                    {PLATFORMS.map((p) => (
                        <Grid item xs={6} sm={3} md={2} key={p.name}>
                            <CloudPlatformIcon {...p} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <WebhookIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready to Sync to the Cloud?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Configure your endpoints and turn your local sensor into a global data stream.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Launch AWS Lab" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="API Documentation" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default CloudIntegrationAPIs;
